import { ChevronLeft } from "lucide-react"
import type * as React from "react"
import { Link } from "react-router"

import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/alert"
import { EmptyState } from "@/components/ui/empty-state"
import { bannerFor } from "@/lib/api-error-message"

import { isReviewGone, REVIEW_QUEUE_PATH } from "./review-gone"

/*
   What the workspace shows instead of itself: while the review loads, and when
   it cannot be loaded at all. Neither knows anything about a review, which is
   why they sit outside the page that does.
*/

/** The explicit way out of a review that is not there, for an empty state's action. */
export function GoToQueueButton({
  to = REVIEW_QUEUE_PATH,
  label = "Go to review queue",
}: {
  to?: string
  label?: string
}): JSX.Element {
  return (
    <Button size="sm" asChild>
      <Link to={to}>{label}</Link>
    </Button>
  )
}

/**
 * A slim bar carrying the way back, so the pane is not a dead end with no exit
 * but the browser's own Back. The workspace header cannot render here — it
 * needs a review — and a page with no route out of it reads as a different
 * application rather than a corner of this one.
 */
function WorkspaceExitFrame({
  children,
  to = REVIEW_QUEUE_PATH,
  label = "Review queue",
}: {
  children: React.ReactNode
  to?: string
  label?: string
}): JSX.Element {
  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* The report's back bar, chevron and all, so every "back" in a review reads alike. */}
      <div className="flex shrink-0 items-center gap-3 border-b border-border bg-surface px-4 py-2">
        <Button size="sm" variant="ghost" asChild>
          <Link to={to}>
            <ChevronLeft className="size-4" aria-hidden="true" />
            {label}
          </Link>
        </Button>
      </div>
      <div className="flex flex-1 items-center justify-center p-8">{children}</div>
    </div>
  )
}

/**
 * The review is not there. One state for every way the console learns that — the
 * workspace's detail query resolving to nothing or failing with 404/403, and the client
 * report finding the same — so a missing review and its missing report look the same and
 * offer the same way out. The title says which of the two was asked for; `to`/`label` are
 * the reader's queue (a client's is `/`, "All reviews").
 */
export function ReviewGoneState({
  title = "This review isn't here",
  to = REVIEW_QUEUE_PATH,
  label = "Review queue",
}: {
  title?: string
  to?: string
  label?: string
}): JSX.Element {
  return (
    <WorkspaceExitFrame to={to} label={label}>
      <EmptyState
        /* A missing review is not a fault: nothing broke, the link is stale.
           The error icon claims otherwise. */
        reason="empty"
        title={title}
        body="The link you followed points at a review that has been archived, or never existed."
        action={<GoToQueueButton to={to} label={`Go to ${label.toLowerCase()}`} />}
      />
    </WorkspaceExitFrame>
  )
}

/**
 * The review could not be loaded, and what to do about it.
 *
 * The action follows the cause. A server that stumbled is worth retrying; a
 * review that is not there will not appear on a second attempt, and offering
 * Retry for it leaves the reader pressing a button that cannot work. That is
 * the one case where the way out is back to the queue.
 */
export function QueryErrorState({
  error,
  onRetry,
}: {
  error: unknown
  onRetry: () => void
}): JSX.Element {
  // A 404/403 reads exactly like a review that resolved to nothing: same words, same way out.
  if (isReviewGone(error)) return <ReviewGoneState />
  const banner = bannerFor(error, "this review")

  return (
    <WorkspaceExitFrame>
      <EmptyState
        reason="error"
        title={banner.title || "Couldn't load this review"}
        body={
          banner.body ||
          "We reached the server but it did not send the review back. Try again in a moment."
        }
        action={
          <Button size="sm" onClick={onRetry}>
            Retry
          </Button>
        }
      />
    </WorkspaceExitFrame>
  )
}

export function WorkspaceSkeleton(): JSX.Element {
  return (
    <div className="flex h-full min-h-0 flex-col" aria-busy="true">
      <div className="flex items-center gap-3 border-b border-border bg-surface px-4 py-3">
        <Skeleton className="size-8 rounded-sm" />
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-4 w-14" />
        <div className="ml-auto flex items-center gap-1.5">
          <Skeleton className="h-8 w-40 rounded-full" />
          <Skeleton className="size-8 rounded-sm" />
          <Skeleton className="size-8 rounded-sm" />
          <Skeleton className="h-9 w-32 rounded-md" />
        </div>
      </div>
      <div className="flex min-h-0 flex-1">
        <div className="flex-1 bg-[color:var(--surface-sunken)] p-6">
          <Skeleton className="mx-auto h-full max-w-[720px] rounded-sm" />
        </div>
        <div className="w-[440px] border-l border-border bg-surface p-4">
          <Skeleton className="mb-3 h-5 w-24" />
          <div className="space-y-2">
            <Skeleton className="h-16 w-full rounded-md" />
            <Skeleton className="h-16 w-full rounded-md" />
            <Skeleton className="h-16 w-full rounded-md" />
          </div>
        </div>
      </div>
      <span className="sr-only">Loading review</span>
    </div>
  )
}
